import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import MoreVert from '@mui/icons-material/MoreVert';

import { GBENSContext } from '../GBENSContainer';

import { ConsigneeDetailsModal, ConsignorDetailsModal } from '../Modals/ConsignmentModals';

import Row from './_Row';

const NeenorAutocomplete = ({ name, modal, placeholder, toolTip, rowProps, disabled }) => {
  const { setCompanyId, referenceData, currentConsignment, currentConsignmentErrors, selectNeenor } =
    useContext(GBENSContext);

  const filter = createFilterOptions();

  const [inputValue, setInputValue] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addNew, setAddNew] = useState(null);

  const valueToUse = useMemo(() => {
    return Object.keys(currentConsignment?.[modal] ?? {}).length > 0 ? currentConsignment[modal] : null;
  }, [currentConsignment]);

  const ACOptions = useMemo(() => {
    if (!Array.isArray(referenceData.consignors) && !Array.isArray(referenceData.consignees)) {
      setCompanyId(sessionStorage.getItem('companyid'));
      return [];
    }
    return modal === 'Consignor'
      ? [
          ...referenceData.consignors,
          ...(currentConsignment?.Consignor?.id < 0 && Object.keys(currentConsignment.Consignor).length > 0
            ? [currentConsignment.Consignor]
            : []),
        ]
      : referenceData?.consignees
        ? [
            ...referenceData?.consignees,
            ...(currentConsignment?.Consignee?.id < 0 && Object.keys(currentConsignment.Consignee).length > 0
              ? [currentConsignment.Consignee]
              : []),
          ]
        : [];
  }, [referenceData, currentConsignment]);

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton onClick={() => setModalOpen(true)} edge="end">
        <MoreVert />
      </IconButton>
    </InputAdornment>
  );

  return (
    <>
      {modal === 'Consignor' ? (
        <ConsignorDetailsModal open={modalOpen} setOpen={setModalOpen} addNew={addNew} setAddNew={setAddNew} />
      ) : (
        <ConsigneeDetailsModal open={modalOpen} setOpen={setModalOpen} addNew={addNew} setAddNew={setAddNew} />
      )}
      <Row
        warning={currentConsignmentErrors?.[modal]}
        name={name}
        toolTip={toolTip}
        rowProps={rowProps}
        component={
          <FormControl variant="standard" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Autocomplete
              variant="standard"
              fullWidth
              inputValue={inputValue}
              value={valueToUse}
              onInputChange={(e, v) => setInputValue(v)}
              options={ACOptions}
              disabled={disabled}
              onChange={(event, newValue) => {
                if (newValue?.inputValue) {
                  if (/^\D{2}\d+/.test(newValue.inputValue)) setAddNew({ eori: newValue.inputValue });
                  else setAddNew({ companyName: newValue.inputValue });
                  setModalOpen(true);
                } else {
                  setAddNew(null);
                  selectNeenor(modal, newValue);
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some(
                  (option) => inputValue === option.companyName || inputValue === option.EORI
                );
                if (inputValue !== '' && !isExisting) {
                  console.log('Not existing pushing ' + inputValue);
                  filtered.push({
                    inputValue,
                    add: `Add "${inputValue}"`,
                  });
                }

                return filtered;
              }}
              getOptionLabel={(option) => {
                // Value selected with enter, right from the input
                if (typeof option === 'string') {
                  return option;
                }
                // Add "xxx" option created dynamically
                if (option.inputValue) {
                  return option.inputValue;
                }

                // Regular option
                return `${option.companyName} ${option.EORI ? `(${option.EORI})` : ''}`;
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    {option.add ? option.add : `${option.companyName} ${option.EORI ? `(${option.EORI})` : ''}`}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  slotProps={{
                    inputLabel: {
                      shrink: false,
                      style: { color: '#00857d', margin: 0, padding: 0, fontSize: 15, fontStyle: 'italic' },
                    },
                  }}
                  {...params}
                  label={!inputValue || inputValue === '' ? placeholder : ''}
                />
              )}
            />
            {endAdornment}
          </FormControl>
        }
      />
    </>
  );
};
NeenorAutocomplete.propTypes = {
  placeholder: PropTypes.string,
  name: PropTypes.string,
  modal: PropTypes.string,
  toolTip: PropTypes.string,
  rowProps: PropTypes.object,
};

export default NeenorAutocomplete;
